import { useEffect } from "react";
import { toast } from "react-toastify";
import type { DataAwareResponse } from "@/types/types";
import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
} from "@tanstack/react-query";
import { logErrorInDev, getErrorMessage } from "@/helper/errorMessages";
import errorNotifications from "@/helper/helper";

type CustomMessage = {
  error?: string;
  success?: string;
};

type Props<TData, TVariables> = UseMutationOptions<
  DataAwareResponse<TData>,
  unknown,
  TVariables,
  unknown
> & {
  customMessage?: CustomMessage;
};

export function useMutationWithValidation<TData, TVariables>({
  customMessage,
  ...restOptions
}: Props<TData, TVariables>): UseMutationResult<DataAwareResponse<TData>, unknown, TVariables> {
  const mutation = useMutation<DataAwareResponse<TData>, unknown, TVariables>({
    ...restOptions,
  });

  useEffect(() => {
    const validationItems = mutation?.data?.validation?.items;

    if (validationItems?.length) {
      errorNotifications(validationItems);
    }
  }, [mutation.data]);

  useEffect(() => {
    if (mutation.isSuccess && customMessage?.success && !mutation.data?.validation?.items.length) {
      toast.success(customMessage.success);
    }
  }, [customMessage?.success, mutation.data?.validation?.items.length, mutation.isSuccess]);

  useEffect(() => {
    if (mutation.isError && mutation.error) {
      logErrorInDev(mutation.error, "Mutation");

      const errorMessage = getErrorMessage(mutation.error);
      toast.error(customMessage?.error || errorMessage);
    }
  }, [mutation.error, mutation.isError, customMessage?.error]);

  return mutation;
}
